/**
 * Tiny offline-action queue.
 *
 * Persists pending driver actions in localStorage so they survive page reloads
 * and the device going offline. On `online` event, flushes the queue with backoff.
 *
 * Each action: { id, type: 'status' | 'proof', orderId, payload, createdAt }
 *   - status: payload = { status, notes? } → POST /api/orders/{id}/status
 *   - proof:  payload = { photoDataUrl?, signatureDataUrl? } → POST /api/orders/{id}/delivery-proof (multipart)
 */
import api from "@/lib/api";

const KEY = "nivara_offline_queue_v1";
const listeners = new Set();

function read() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

function write(items) {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
    listeners.forEach((l) => l(items));
  } catch {
    /* localStorage full or unavailable — fail silently */
  }
}

export function getQueue() {
  return read();
}

export function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function enqueue(action) {
  const items = read();
  items.push({
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    createdAt: Date.now(),
    ...action,
  });
  write(items);
}

function dataUrlToBlob(dataUrl) {
  if (!dataUrl) return null;
  const [meta, b64] = dataUrl.split(",");
  const mime = (meta.match(/data:([^;]+)/) || [])[1] || "image/png";
  const bin = atob(b64);
  const len = bin.length;
  const arr = new Uint8Array(len);
  for (let i = 0; i < len; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

async function executeOne(item) {
  if (item.type === "status") {
    await api.post(`/orders/${item.orderId}/status`, item.payload);
  } else if (item.type === "proof") {
    const fd = new FormData();
    const photo = dataUrlToBlob(item.payload.photoDataUrl);
    const sig = dataUrlToBlob(item.payload.signatureDataUrl);
    if (photo) fd.append("photo", photo, "photo.png");
    if (sig) fd.append("signature", sig, "signature.png");
    await api.post(`/orders/${item.orderId}/delivery-proof`, fd);
    // After proof, finalize status if requested
    if (item.payload.thenStatus) {
      await api.post(`/orders/${item.orderId}/status`, {
        status: item.payload.thenStatus,
        notes: "Zugestellt mit Foto/Unterschrift (offline-Sync)",
      });
    }
  }
}

let flushing = false;
export async function flush() {
  if (flushing) return { skipped: true };
  if (!navigator.onLine) return { offline: true };
  flushing = true;
  let success = 0;
  let failed = 0;
  try {
    let queue = read();
    while (queue.length > 0) {
      const head = queue[0];
      try {
        await executeOne(head);
        success += 1;
        queue = read().slice(1);
        write(queue);
      } catch (e) {
        // Stop on first failure (next online tick / manual retry)
        failed += 1;
        break;
      }
    }
  } finally {
    flushing = false;
  }
  return { success, failed };
}

// Auto-flush on reconnect
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { flush(); });
}

export function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}
